import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  CollectionResponse,
  CreateOrganizationPayload,
  MyOrganization,
  Organization,
  OrganizationAnalytics,
  OrganizationHealthDashboard,
  PaginationQuery,
  QualityManagementProcessIndicatorsResponse,
  QualityManagementRevenueResponse,
  QualityManagementTreatmentsResponse,
  RevenueClinician,
  Response,
} from "~/types/api";
import type {
  Administration,
  CreateAdministrationPayload,
  OrganizationEmployee,
  OrganizationLocation,
  OrganizationStats,
  AgbRegisterReferrerLookup,
  Referrer,
  ReferrerPayload,
  SelectableReferrer,
  SelectableThirdParty,
  ThirdParty,
  ThirdPartyPayload,
  UpdateOrganizationPayload,
  ZorgmailAddressBookSearchResponse,
} from "~/types/api";

export function createOrganizationApi({
  getRequest,
  postRequest,
  putRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  /** Fetches all organizations. */
  const organizations = (options?: { omitContextHeaders?: boolean }) =>
    getRequest<Organization[]>(`/organizations`, options);

  /** Creates a new organization. */
  const createOrganization = (data: CreateOrganizationPayload) =>
    postRequest<Response<Organization>>(`/organizations`, data);

  /** Fetches administrations for a specific organization. */
  const organizationAdministrations = (organizationId: string) =>
    getRequest<CollectionResponse<Administration>>(`/organizations/${organizationId}/administrations`);

  /** Fetches the current user's organization. */
  const myOrganization = () => getRequest<Response<MyOrganization>>("/my-organization");

  /** Fetches statistics for the current user's organization. */
  const myOrganizationStats = () => getRequest<Response<OrganizationStats>>("/my-organization/stats");

  /** Updates the current user's organization. */
  const updateMyOrganization = (data: UpdateOrganizationPayload) =>
    putRequest<Response<MyOrganization>>("/my-organization", data);

  /** Tests the current organization's QDNA credentials. */
  const testMyOrganizationQdnaCredentials = () =>
    postRequest<Response<MyOrganization>>("/my-organization/qdna-credentials/test");

  /** Fetches all locations for the current user's organization. */
  const myOrganizationLocations = () =>
    getRequest<CollectionResponse<OrganizationLocation>>("/my-organization/locations");

  /** Fetches a paginated list of employees for the current user's organization. */
  const myOrganizationEmployees = (pagination?: PaginationQuery) =>
    getPaginationRequest<OrganizationEmployee>("/my-organization/employees", pagination);

  /** Fetches all administrations for the current user's organization. */
  const myOrganizationAdministrations = () =>
    getRequest<CollectionResponse<Administration>>("/my-organization/administrations");

  /** Creates a new administration for the current user's organization. */
  const createAdministration = (data: CreateAdministrationPayload) =>
    postRequest<Response<Administration>>("/my-organization/administrations", data);

  /** Updates an existing administration. */
  const updateAdministration = (id: string, data: CreateAdministrationPayload) =>
    putRequest<Response<Administration>>(`/my-organization/administrations/${id}`, data);

  /** Fetches all third parties for the current user's organization. */
  const myOrganizationThirdParties = () => getRequest<CollectionResponse<ThirdParty>>("/my-organization/third-parties");

  /** Fetches the active third parties that can be selected for a treatment. */
  const selectableThirdParties = () =>
    getRequest<CollectionResponse<SelectableThirdParty>>("/my-organization/third-parties/selectable");

  /** Creates a new third party for the current user's organization. */
  const createThirdParty = (data: ThirdPartyPayload) =>
    postRequest<Response<ThirdParty>>("/my-organization/third-parties", data);

  /** Updates an existing third party. */
  const updateThirdParty = (id: string, data: ThirdPartyPayload) =>
    putRequest<Response<ThirdParty>>(`/my-organization/third-parties/${id}`, data);

  /** Fetches all referrers for the current user's organization. */
  const myOrganizationReferrers = () => getRequest<CollectionResponse<Referrer>>("/my-organization/referrers");

  /** Fetches the active referrers that can be selected for a treatment referral. */
  const selectableReferrers = () =>
    getRequest<CollectionResponse<SelectableReferrer>>("/my-organization/referrers/selectable");

  /** Looks up a referrer in the AGB register by full AGB code, with the referrer of the organization that uses it. */
  const lookupReferrerAgbRegister = (agbCode: string) =>
    getRequest<Response<AgbRegisterReferrerLookup>>("/my-organization/referrers/agb-register", {
      params: { agb_code: agbCode },
      suppressValidationToast: true,
    });

  const searchZorgmailAddressBook = (query: string, page: number) =>
    getRequest<ZorgmailAddressBookSearchResponse>("/my-organization/zorgmail-address-book", {
      params: { query, page },
      suppressValidationToast: true,
    });

  /** Creates a new referrer for the current user's organization. The response body is the bare resource, not data-wrapped. */
  const createReferrer = (data: ReferrerPayload) =>
    postRequest<Referrer>("/my-organization/referrers", data, { suppressValidationToast: true });

  /** Updates an existing referrer. The response body is the bare resource, not data-wrapped. */
  const updateReferrer = (id: string, data: ReferrerPayload) =>
    putRequest<Referrer>(`/my-organization/referrers/${id}`, data, { suppressValidationToast: true });

  /** Assigns an administration to a location. */
  const assignAdministrationToLocation = (locationId: number, administrationId: string) =>
    putRequest<Response<OrganizationLocation>>(`/my-organization/locations/${locationId}/administration`, {
      administration_id: administrationId,
    });

  /** Fetches analytics data for an organization within a date range. */
  const getOrganizationAnalytics = (organizationId: string, params: { start_date: string; end_date: string }) =>
    getRequest<{ data: OrganizationAnalytics }>(`/organizations/${organizationId}/analytics`, { params }).then(
      (response) => response.data,
    );

  /** Fetches health dashboard metrics for an organization. */
  const getOrganizationHealthDashboard = (organizationId: string) =>
    getRequest<Response<OrganizationHealthDashboard>>(`/organizations/${organizationId}/health`).then(
      (response) => response.data,
    );

  /** Fetches quality management process indicators. */
  const fetchQualityManagementProcessIndicators = (queryString: string) =>
    getRequest<Response<QualityManagementProcessIndicatorsResponse>>(
      `/quality-management/process-indicators?${queryString}`,
    );

  /** Fetches quality management treatment data. */
  const fetchQualityManagementTreatments = (queryString: string) =>
    getRequest<Response<QualityManagementTreatmentsResponse>>(`/quality-management/treatments?${queryString}`);

  /** Fetches quality management revenue data. */
  const fetchQualityManagementRevenue = (queryString: string) =>
    getRequest<Response<QualityManagementRevenueResponse>>(`/quality-management/revenue?${queryString}`);

  const fetchRevenueClinicians = (pagination: PaginationQuery, locationId?: number) =>
    getPaginationRequest<RevenueClinician>(
      "/quality-management/revenue/clinicians",
      pagination,
      undefined,
      undefined,
      locationId === undefined ? [] : [{ column: "location", value: String(locationId) }],
    );

  return {
    organizations,
    createOrganization,
    organizationAdministrations,
    myOrganization,
    myOrganizationStats,
    updateMyOrganization,
    testMyOrganizationQdnaCredentials,
    myOrganizationLocations,
    myOrganizationEmployees,
    myOrganizationAdministrations,
    createAdministration,
    updateAdministration,
    myOrganizationThirdParties,
    selectableThirdParties,
    createThirdParty,
    updateThirdParty,
    myOrganizationReferrers,
    selectableReferrers,
    lookupReferrerAgbRegister,
    searchZorgmailAddressBook,
    createReferrer,
    updateReferrer,
    assignAdministrationToLocation,
    getOrganizationAnalytics,
    getOrganizationHealthDashboard,
    fetchQualityManagementProcessIndicators,
    fetchQualityManagementTreatments,
    fetchQualityManagementRevenue,
    fetchRevenueClinicians,
  };
}

export type OrganizationApi = ReturnType<typeof createOrganizationApi>;
